/* TopProductsWidget — admin Overview tile.
 *
 * Reads /api/admin/services/top-products and renders a small leaderboard
 * of the most-viewed Services PDPs in a configurable rolling window.
 * Each row links straight to the PDP so an admin can preview what
 * customers are eyeing this week.
 *
 * Period selector: 7 / 30 / 90 days. Period defaults to 7.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { TrendingUp, Eye, Users, Package, ExternalLink, RefreshCw } from 'lucide-react';

const API = `${(process.env.CRA_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL)}/api`;

export default function TopProductsWidget() {
  const [days, setDays] = useState(7);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ events: 0, unique_slugs: 0, unique_visitors: 0 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(
        `${API}/admin/services/top-products?days=${days}&limit=8`,
        { credentials: 'include' },
      );
      if (r.ok) {
        const d = await r.json();
        setRows(d.rows || []);
        setTotals(d.totals || { events: 0, unique_slugs: 0, unique_visitors: 0 });
      }
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const maxViews = Math.max(1, ...(rows.map((r) => r.views || 0)));

  return (
    <section
      data-testid="admin-top-products-widget"
      className="rounded-2xl border border-gray-200 bg-white overflow-hidden"
    >
      <header className="px-6 py-4 flex items-center justify-between bg-[#f8f8fa] border-b border-gray-100 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-[#c9a227]" />
          <div>
            <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-[#6E6E73]">
              ZHELTA Services analytics
            </div>
            <div className="text-[15px] font-extrabold text-[#0e0e10]">
              Top viewed products
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            data-testid="top-products-period"
            className="text-[12px] rounded-full border border-[#E5E5E7] px-3 py-1.5 outline-none focus:border-[#c9a227]"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <button
            type="button"
            onClick={load}
            data-testid="top-products-refresh"
            className="inline-flex items-center gap-1 text-[12px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full border border-[#E5E5E7] hover:bg-[#fff8e7] hover:border-[#c9a227]/50 transition-colors"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </header>

      {/* Totals strip */}
      <div className="px-6 py-3 grid grid-cols-3 gap-3 border-b border-gray-100">
        <Totals icon={Eye}     label="PDP views"     value={totals.events} />
        <Totals icon={Package} label="Products seen" value={totals.unique_slugs} />
        <Totals icon={Users}   label="Unique IPs"    value={totals.unique_visitors} />
      </div>

      {/* Leaderboard */}
      <div className="p-4">
        {loading && rows.length === 0 ? (
          <div className="text-center py-8 text-[12px] text-[#6E6E73]">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-center py-10" data-testid="top-products-empty">
            <Package className="w-8 h-8 text-[#c9a227] mx-auto mb-2 opacity-50" />
            <div className="text-[13px] font-bold text-[#0e0e10]">No PDP views in this period yet.</div>
            <p className="text-[12px] text-[#6E6E73] mt-1">
              Once visitors start opening product pages on /services, you'll
              see the most-viewed SKUs ranked here.
            </p>
          </div>
        ) : (
          <ol className="divide-y divide-gray-100">
            {rows.map((r, idx) => (
              <li
                key={r.slug}
                data-testid={`top-product-row-${r.slug}`}
                className="py-3 first:pt-1 last:pb-1 flex items-center gap-3"
              >
                <span className="shrink-0 w-6 h-6 inline-flex items-center justify-center rounded-full bg-[#fff8e7] text-[#a8871a] text-[11px] font-extrabold tabular-nums">
                  {idx + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <a
                      href={`/services/p/${encodeURIComponent(r.slug)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[13px] font-extrabold text-[#0e0e10] truncate hover:text-[#a8871a] transition-colors inline-flex items-center gap-1.5 max-w-[80%]"
                    >
                      <span className="truncate">{r.name}</span>
                      <ExternalLink className="w-3 h-3 shrink-0 opacity-60" />
                    </a>
                    <span className="text-[12px] font-extrabold tabular-nums text-[#0e0e10] shrink-0">
                      {r.views} view{r.views === 1 ? '' : 's'}
                    </span>
                  </div>
                  <div className="h-1 rounded-full bg-[#F5F5F7] overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${Math.round((r.views / maxViews) * 100)}%`,
                        background: 'linear-gradient(90deg,#e6c35a 0%,#c9a227 100%)',
                      }}
                    />
                  </div>
                  <div className="flex items-center gap-3 mt-1.5 text-[11px] text-[#6E6E73]">
                    <span>{r.unique_visitors} unique visitor{r.unique_visitors === 1 ? '' : 's'}</span>
                    <span className="text-[#9aa0a6]">·</span>
                    <span className="font-mono text-[10px] text-[#9aa0a6] truncate">{r.slug}</span>
                  </div>
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>
    </section>
  );
}

function Totals({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-2.5">
      <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-[#fff8e7] text-[#a8871a]">
        <Icon className="w-3.5 h-3.5" strokeWidth={2.2} />
      </span>
      <div className="min-w-0">
        <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#6E6E73]">{label}</div>
        <div className="text-[16px] font-extrabold tabular-nums text-[#0e0e10]">{value}</div>
      </div>
    </div>
  );
}
